import { useState, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Theme,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Popper,
  Tab,
  Tabs,
  Toolbar,
  Divider,
} from '@mui/material';
import { CloudDownload, ArrowDropDown } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useGetForecastAnalyticsQuery,
  useGetWinLossAnalyticsQuery,
  useGetProjectTypesQuery,
  useGetSubscribersQuery,
  useGetTeamContactsQuery,
  useGetTeamsQuery,
  useLazyGetForecastAnalyticsExportQuery,
} from 'store/api';
import { Contact, ProjectType, Subscriber, Team } from 'store/api/models';
import {
  JFormik,
  KpiCard,
  numToPercent,
  useBreakpointQuery,
} from '@centerpiece/components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { useIndexContext } from 'contexts/index.context';
import { TabIndexTableToolbar } from '@centerpiece/components/src/components/table/TabIndexTableToolbar';
import { FactorAnalysisTable } from './analytics_tabs/FactorAnalysisTable';
import { FinancialPerformanceTable } from './analytics_tabs/FinancialPerformanceTable';
import { useAnalyticContext } from 'contexts/analytic.context';
import { WinRateKpiCards } from './analytics_tabs/WinRateKpiCards';
import { ProjectUpliftKpiCards } from './analytics_tabs/ProjectUpliftKpiCards';
import { BidVolumeWinRateLineChart } from './analytics_tabs/BidVolumeWinRateLineChart';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const UpliftAnalyticsIndexPage = () => {
  const {
    contactRecord,
    team,
    setTeam,
    individual,
    setIndividual,
    projectType,
    setProjectType,
    teams,
    projectTypes,
    subscribers,
  } = useAnalyticContext();

  const { data: forecast = [] } = useGetForecastAnalyticsQuery({
    params: {
      team: team?.id,
      individual: individual?.id,
    },
  });

  const revenueData = useMemo(() => {
    const endDate = moment().add(12, 'months'); // one year in the future
    const startDate = moment();
    const data: any = {};

    // Loop through each month from the start date to the current date
    let currentDate = moment(startDate);
    while (currentDate.isSameOrBefore(endDate)) {
      const yearMonth = currentDate.format('YYYY-MM');
      data[yearMonth] = {
        awarded_rev: 0,
        pre_award_rev: 0,
        unknown: 0,
      };

      // Move to the next month
      currentDate.add(1, 'month');
    }
    data['No Date'] = {
      awarded_rev: 0,
      pre_award_rev: 0,
      unknown: 0,
    };
    forecast?.forEach((f: any) => {
      const awarded_rev = f.awarded ? f.revenue_sum : 0;
      const pre_award_rev = f.awarded === false ? f.revenue_sum : 0;
      const unknown = f.awarded === null ? f.revenue_sum : 0;
      if (f.year) {
        const key = `${f?.year}-${f.month > 9 ? f.month : `0${f.month}`}`;
        data[key] = {
          awarded_rev: data[key]?.awarded_rev + awarded_rev,
          pre_award_rev: data[key]?.pre_award_rev + pre_award_rev,
          unknown: data[key]?.unknown + unknown,
        };
      } else {
        const key = 'No Date';
        data[key] = {
          awarded_rev: data[key]?.awarded_rev + awarded_rev,
          pre_award_rev: data[key]?.pre_award_rev + pre_award_rev,
          unknown: data[key]?.unknown + unknown,
        };
      }
    });
    return {
      labels: Object.keys(data),
      datasets: [
        {
          label: 'Revenue from awarded jobs',
          data: Object.values(data)?.map((d: any) => d.awarded_rev),
          backgroundColor: '#76B041',
        },
        {
          label: 'Expected revenue from quotes/bids',
          data: Object.values(data)?.map((d: any) => d.pre_award_rev),
          backgroundColor: '#163B3F',
        },
        {
          label: 'Revenue from projects with no stage',
          data: Object.values(data)?.map((d: any) => d.unknown),
          backgroundColor: 'gray',
        },
      ],
    };
  }, [forecast]);

  const [exportData, exportResult] = useLazyGetForecastAnalyticsExportQuery({
    refetchOnFocus: false,
  });

  const mdDown = useBreakpointQuery('md-down');
  const smDown = useBreakpointQuery('sm-down');
  const [value, setValue] = useState<number>(1);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleMenuItemClick = (event: any, index: number) => {
    handleChange(event, index);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };
  // const sortedTabs = ['Win Rate', 'Project Uplift'];
  return (
    <Grid container spacing={2} sx={{ mb: 5 }}>
      <Grid item xs={12}>
        <Grid item xs={12} sx={{ py: 2 }}>
          <Grid
            container
            direction="row"
            alignItems="center"
            spacing={2}
            flexWrap={'nowrap'}
          >
            <Grid item sx={{ pr: 1 }}>
              <Typography variant="h6">Analytics Filters:</Typography>
            </Grid>
            <Grid flex={1} item>
              <JFormik
                initialValues={{}}
                form={[
                  {
                    name: 'type',
                    itemType: 'autocomplete',
                    label: 'Project Type',
                    value: projectType,
                    gridProps: {
                      xs: 3,
                    },
                    options: [
                      { id: 0, name: 'All' },
                      ...(projectTypes.filter(p => p.win_loss) || []),
                    ],
                    jItemProps: {
                      onChange: (type: ProjectType) => {
                        setProjectType(type || ({ id: 0, name: 'All' } as any));
                      },
                      // InputProps: {
                      //   sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                      // },
                    },
                  },
                  {
                    precondition: () =>
                      contactRecord?.form?.authorization === 'Executive' ||
                      contactRecord?.form?.authorization ===
                        'Organization Member' ||
                      contactRecord?.form?.authorization === 'Team Leader',
                    name: 'team',
                    itemType: 'autocomplete',
                    label: 'Team',
                    disabled:
                      contactRecord?.form?.authorization === 'Team Leader',
                    value: team,
                    gridProps: {
                      xs: 3,
                    },
                    options: [{ id: 0, name: 'All' }, ...teams],
                    jItemProps: {
                      onChange: (team: Team) => {
                        setTeam(team || ({ id: 0, name: 'All' } as any));
                      },
                      // InputProps: {
                      //   sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                      // },
                    },
                  },
                  {
                    name: 'contact',
                    itemType: 'autocomplete',
                    label: 'Individual',
                    value: individual,
                    options: [
                      { id: 0, name: 'All' },
                      ...subscribers
                        ?.filter(
                          c =>
                            c.contact_r?.form?.team?.id === team.id ||
                            team.id === 0
                        )
                        ?.map(c => ({
                          ...c.contact_r,
                          name: `${c.contact_r.firstname} ${c.contact_r.lastname}`,
                        })),
                    ],
                    gridProps: { xs: 3 },
                    jItemProps: {
                      onChange: (contact: any) =>
                        setIndividual(
                          contact || ({ id: 0, name: 'All' } as any)
                        ),
                      // InputProps: {
                      //   sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                      // },
                    },
                  },
                ]}
              />
            </Grid>
          </Grid>
        </Grid>
        <Divider />
      </Grid>
      <Grid item xs={12} sx={{ pt: '0 important!' }}>
        {/* <Toolbar sx={{ px: { xs: 1, sm: 0 }, py: { md: 1 } }} disableGutters>
          {!mdDown ? (
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              spacing={2}
              // flexWrap={'nowrap'}
            >
              <Grid item md={12} lg={6} xl={7}>
                <Tabs
                  value={value}
                  variant={'scrollable'}
                  onChange={(event: any, value: any) =>
                    handleChange(event, value)
                  }
                  sx={{
                    width: '100%',
                    '& .MuiTabs-flexContainer': {
                      pl: 0,
                    },
                  }}
                >
                  {sortedTabs.map((tab, index) => (
                    <Tab
                      id={`tab-${index}`}
                      className={`tab-${index}`}
                      key={index}
                      label={
                        <Typography
                          variant="h6"
                          id="tableTitle"
                          component="span"
                        >
                          {tab}&nbsp;
                        </Typography>
                      }
                    />
                  ))}
                </Tabs>
              </Grid>
              <Grid
                item
                md={12}
                lg={6}
                xl={5}
                // sx={{ marginLeft: 'auto', flex: 1, pb: 2 }}
                // flexWrap={'nowrap'}
              >
                <Grid
                  container
                  direction="row-reverse"
                  alignItems="flex-start"
                  spacing={1}
                  flexWrap={'nowrap'}
                ></Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              // flexWrap={'nowrap'}
            >
              <Grid item flexGrow={2}>
                <Chip
                  ref={anchorRef}
                  // size="small"
                  color="primary"
                  label={sortedTabs[value] || 'Select tab'}
                  onClick={handleToggle}
                  onDelete={handleToggle}
                  deleteIcon={<ArrowDropDown />}
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Popper
                sx={{ zIndex: 10 }}
                open={open}
                anchorEl={anchorRef.current}
                transition
                placement={'bottom-start'}
                disablePortal
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                  >
                    <Paper
                      sx={{
                        px: 4,
                        py: 2,
                      }}
                    >
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList autoFocusItem>
                          <small style={{ padding: 1 }}>
                            * select option below
                          </small>
                          {sortedTabs.map((tab, index) => (
                            <MenuItem
                              key={`mobile-tab-${index}`}
                              selected={index === value}
                              onClick={event =>
                                handleMenuItemClick(event, index)
                              }
                              sx={{ py: 1.5 }}
                            >
                              {tab}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Grid>
          )}
        </Toolbar> */}
        {value === 0 && <WinRateKpiCards />}
        {value === 1 && <ProjectUpliftKpiCards />}
      </Grid>
      {false && value === 0 && projectType?.name === 'Bid' && (
        <BidVolumeWinRateLineChart />
      )}
      <Grid item xs={12}>
        {value === 0 && <FactorAnalysisTable />}
        {value === 1 && <FinancialPerformanceTable />}
      </Grid>
    </Grid>
  );
};
